
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import Layout from "./components/layout";
import BabelRepl from "./components/babel-repl";
import LiveEditor from "./components/live-editor";
import SoloEditor from "./components/solo-editor";
import { Logo } from "./components/shared";
import reactUsage from "./assets/react-usage.png";
import baseTheme from "./theme";
export const theme = baseTheme;
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Head = makeShortcode("Head");
const Steps = makeShortcode("Steps");
const Notes = makeShortcode("Notes");
const layoutProps = {
  theme
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    {
      /* Begin setup */
    }



    <Head mdxType="Head">
  <title>Slides | Intro to React</title>
  <link rel="shortcut icon" type="image/x-icon" href="https://instructure-uploads.s3.amazonaws.com/account_158020000000000001/attachments/43718/flatiron-favicon.ico" />
    </Head>
    {
      /* End setup */
    }
    {
      /* Begin Slide */
    }
    <Layout mdxType="Layout">
      <h1>{`Intro to React`}</h1>
      <Logo mdxType="Logo" />
    </Layout>
    {
      /* End Slide */
    }
    <hr></hr>
    {
      /* Begin Slide */
    }
    <Layout mdxType="Layout">
      <h1>{`✅ Objectives`}</h1>
      <p>{`We'll learn...`}</p>
      <Steps mdxType="Steps">
        <ul>
          <li parentName="ul">{`Why you should learn React`}</li>
          <li parentName="ul">{`React's philosophy`}</li>
          <li parentName="ul">{`How React works`}</li>
        </ul>
      </Steps>
    </Layout>
    {
      /* End Slide */
    }
    <hr></hr>
    {
      /* Begin Slide */
    }
    <Layout mdxType="Layout">
      <h1>{`🤔 Why React?`}</h1>
      <img src={reactUsage} alt="component diagram" style={{
        height: "600px"
      }} />
      <p><em parentName="p">{`—`}{` `}<a parentName="em" {...{
            "href": "https://2020.stateofjs.com/en-US/technologies/front-end-frameworks/"
          }}>{`Frontend Framework Usage, 2020 State of JS Survey`}</a></em></p>
    </Layout>
    <Notes mdxType="Notes">
      <ul>
        <li parentName="ul">{`One key reason we teach React is simply its popularity in the marketplace`}</li>
        <li parentName="ul">{`But React is also popular for a reason`}</li>
        <li parentName="ul">{`It solved a lot of problems developers had in the past with other frontend libraries`}</li>
        <li parentName="ul">{`It also has some good tricks up its sleeve to solve these problems in a way that doesn't sacrifice performance`}</li>
      </ul>
    </Notes>
    {
      /* End Slide */
    }
    <hr></hr>
    {
      /* Begin Slide */
    }
    <Layout mdxType="Layout">
      <h1>{`🤔 Why React?`}</h1>
      <p>{`Writing vanilla JavaScript is hard!
Particularly when it comes to:`}</p>
      <ul>
        <li parentName="ul">{`Creating/Updating DOM elements`}</li>
        <li parentName="ul">{`Code organization (separation of concerns)`}</li>
      </ul>
    </Layout>
    <Notes mdxType="Notes">
      <p>{`When I asked some of you about your vanilla JS projects, these were some of the things you mentioned having challenges with.`}</p>
      <ul>
        <li parentName="ul">{`You're not alone! These are hard problems, and something JS developers struggled with for a long time`}</li>
        <li parentName="ul">{`React gives us code to solve these problems by `}<em parentName="li">{`abstracting`}</em>{` the work of manipulating the DOM`}</li>
        <li parentName="ul">{`It also has strong opinions about how your code should be organized`}
          <ul parentName="li">
            <li parentName="ul">{`If you "buy into" React's philosophy, you're gonna have a good time with React...`}</li>
            <li parentName="ul">{`If you fight against it, you'll struggle`}</li>
          </ul>
        </li>
      </ul>
    </Notes>
    {
      /* End Slide */
    }
    <hr></hr>
    {
      /* Begin Slide */
    }
    <Layout mdxType="Layout">
      <h1>{`💡 React Philosophy`}</h1>
      <ul>
        <li parentName="ul">{`Uses `}<strong parentName="li">{`declarative syntax (JSX)`}</strong>
          <ul parentName="li">
            <li parentName="ul">{`Makes it easier to work with the DOM`}</li>
            <li parentName="ul">{`Clearer connection between the code we write and what is displayed in the browser`}</li>
          </ul>
        </li>
        <li parentName="ul">{`Break down complex UI into smaller `}<strong parentName="li">{`components`}</strong>
          <ul parentName="li">
            <li parentName="ul">{`Separation of concerns`}</li>
            <li parentName="ul">{`Easier to maintain`}</li>
          </ul>
        </li>
      </ul>
    </Layout>
    {
      /* End Slide */
    }
    <hr></hr>
    {
      /* Begin Slide */
    }
    <Layout mdxType="Layout">
      <h1>{`🎉 Declarative Syntax`}</h1>
      <Steps mdxType="Steps">
        <p>{`Instead of describing `}<em parentName="p">{`how to do something`}</em>{`...`}</p>
        <div>
          <SoloEditor mdxType="SoloEditor">
            <pre><code parentName="pre" {...{
                "className": "language-js"
              }}>{`const h1 = document.createElement("h1");
h1.id = "main";
h1.className = "blue";
h1.textContent = "Hello!";
`}</code></pre>
          </SoloEditor>
        </div>
        <p>{`...just describe `}<em parentName="p">{`what we want`}</em>{`:`}</p>
        <div>
          <SoloEditor mdxType="SoloEditor">
            <pre><code parentName="pre" {...{
                "className": "language-js"
              }}>{`const h1 = (
  <h1 id="main" className="blue">
    Hello from JSX!
  </h1>
);
`}</code></pre>
          </SoloEditor>
        </div>
      </Steps>
    </Layout>
    <Notes mdxType="Notes">
      <p>{`Has anyone heard about declarative coding vs imperative code?`}</p>
      <ul>
        <li parentName="ul">{`Ask for an example`}</li>
        <li parentName="ul">{`Chipotle analogy: ordering a burrito vs making one by hand`}</li>
      </ul>
      <p>{`Declarative code lets us write something more `}<em parentName="p">{`abstract`}</em>{`.`}</p>
      <ul>
        <li parentName="ul">{`We don't have to worry about the low-level details of what's happening under the hood`}</li>
        <li parentName="ul">{`We can focus more on the end result (in this case, what's being rendered to the DOM)`}</li>
      </ul>
    </Notes>
    {
      /* End Slide */
    }
    <hr></hr>
    <Layout mdxType="Layout">
      <h1>{`🤔 But How?`}</h1>
      <p>{`Under the hood, React apps use a tool called Babel to `}<strong parentName="p">{`transpile`}</strong>{` the JSX syntax to valid JavaScript:`}</p>
      <Steps mdxType="Steps">
        <ul>
          <li parentName="ul">{`We write JSX`}</li>
          <li parentName="ul">{`Babel `}<strong parentName="li">{`transpiles`}</strong>{` JSX into valid JavaScript`}</li>
          <li parentName="ul">{`That `}<strong parentName="li">{`transpiled`}</strong>{` code is what actually runs in the browser`}</li>
        </ul>
      </Steps>
    </Layout>
    {
      /* End Slide */
    }
    <hr></hr>
    {
      /* Begin Slide */
    }
    <Layout mdxType="Layout">
      <h2>{`JSX ➡️ Babel ➡️ JS`}</h2>
      <BabelRepl mdxType="BabelRepl">
        <pre><code parentName="pre" {...{
            "className": "language-js"
          }}>{`const h1 = (
  <h1 id="main" className="blue">
    Hello from JSX!
  </h1>
);
`}</code></pre>
      </BabelRepl>
    </Layout>
    <Notes mdxType="Notes">
      <p>{`In this window, the left-hand side is JSX (JavaScript XML). It looks like HTML, but it's not!
This is a special kind of markup that we use to tell React how we want the DOM to look.`}</p>
      <p>{`We write JSX in our JavaScript files, and under the hood, Babel turns it into valid JavaScript
code before the browser runs it. If tried running the code on the left in the browser you'd get a syntax error.
But after Babel is done with it, it's now valid syntax.`}</p>
      <p>{`You don't have to worry too much about this; you can just use JSX in React without thinking about it.
But it's good to know what's happening. At the end of the day, it's all still JavaScript.`}</p>
    </Notes>
    {
      /* End Slide */
    }
    <hr></hr>
    {
      /* Begin Slide */
    }
    <Layout mdxType="Layout">
      <h1>{`🛠 Components`}</h1>
      <p>{`Components are the building blocks of React. A `}<strong parentName="p">{`component`}</strong>{` is a function that:`}</p>
      <ul>
        <li parentName="ul">{`Takes in some `}<strong parentName="li">{`raw data (props)`}</strong></li>
        <li parentName="ul">{`Returns `}<strong parentName="li">{`user interface (JSX)`}</strong></li>
      </ul>
      <SoloEditor mdxType="SoloEditor">
        <pre><code parentName="pre" {...{
            "className": "language-js"
          }}>{`function Header(props) {
  return (
    <div>
      <h3>{props.title}</h3>
      <h4>Subtitle: {props.subtitle}</h4>
    </div>
  );
}
`}</code></pre>
      </SoloEditor>
    </Layout>
    {
      /* End Slide */
    }
    <hr></hr>
    {
      /* Begin Slide */
    }
    <Layout mdxType="Layout">
      <LiveEditor mdxType="LiveEditor">
        <pre><code parentName="pre" {...{
            "className": "language-js"
          }}>{`function Header(props) {
  return (
    <div>
      <h3>{props.title}</h3>
      <h4>Subtitle: {props.subtitle}</h4>
    </div>
  );
}

ReactDOM.render(
  <Header title="Let's Learn React" subtitle="Components" />,
  document.getElementById("root")
);
`}</code></pre>
      </LiveEditor>
    </Layout>
    <Notes mdxType="Notes">
      <p>{`Here's an example of a React component in code. On the left is the code we write.
On the right is the HTML that the browser renders.`}</p>
      <p>{`Think of a component like a `}<strong parentName="p">{`template`}</strong>{` for how we want to display some data.`}</p>
      <p>{`As React developers, your job is to take some raw data (a string, an object, an array, etc) and decide
how a user should interact with that data (how it should be displayed on the page).`}</p>
      <p>{`Components are what make this possible!`}</p>
      <p>{`The syntax might take some getting used to, but remember, in the end it's all just JavaScript.
Think of writing `}<inlineCode parentName="p">{`<Header props="something">`}</inlineCode>{` like us telling React to call the function `}<inlineCode parentName="p">{`Header`}</inlineCode>{`,
and pass one argument of an object with key-value pairs. (we could even write it out this way and it'd still work)`}</p>
    </Notes>
    {
      /* End Slide */
    }
    <hr></hr>
    {
      /* Begin Slide */
    }
    <Layout mdxType="Layout">
      <h1>{`👷‍♀️ Component Composition`}</h1>
      <img src="https://curriculum-content.s3.amazonaws.com/react/yelp-components.png" alt="component diagram" style={{
        height: "600px"
      }} />
    </Layout>
    <Notes mdxType="Notes">
      <p>{`The last example we saw was a simple React component, but a typical React app can have dozens or even hundreds of components.
The great thing about components is that we can `}<strong parentName="p">{`compose`}</strong>{` them (make them work together) to put together complex layouts.
(Even this slideshow is just a bunch of React components!)`}</p>
      <p>{`The image here is part of Yelp's website, and an example of some of the components they might use to put together an app like this.
Take note of how components let us:`}</p>
      <ul>
        <li parentName="ul">{`separate different areas of concern (navigation vs map view vs list view)`}</li>
        <li parentName="ul">{`create reusable templates (we can re-use the same code from the ResultItem component to display multiple results)`}</li>
      </ul>
    </Notes>
    {
      /* End Slide */
    }
    <hr></hr>
    {
      /* Begin Slide */
    }
    <Layout mdxType="Layout">
      <LiveEditor mdxType="LiveEditor">
        <pre><code parentName="pre" {...{
            "className": "language-js"
          }}>{`/* Component 1 */
function Article(props) {
  return (
    <div>
      <h3>{props.title}</h3>
      <h4>Subtitle: {props.subtitle}</h4>
      <hr />
      <Comment content="First!" />
      <Comment content="What a great article." />
    </div>
  );
}

/* Component 2 */
function Comment(props) {
  return <p>{props.content}</p>;
}

/* Render the whole application */
ReactDOM.render(
  <Article title="Let's Learn React" subtitle="Components" />,
  document.getElementById("root")
);
`}</code></pre>
      </LiveEditor>
    </Layout>
    <Notes mdxType="Notes">
      <p>{`Here's an example of what the code looks like for composing multiple components together.
An article can have many comments, so the we're using the Comment component within the Article component.`}</p>
      <p>{`Each component is responsible for its own template, but its "parent" (whichever component returns it)
is responsible for providing its data.`}</p>
      <p>{`We're `}<em parentName="p">{`reusing`}</em>{` the Comment component to make a couple of `}<inlineCode parentName="p">{`<p>`}</inlineCode>{` tags, each with their own content.`}</p>
    </Notes>
    {
      /* End Slide */
    }
    <hr></hr>
    {
      /* Begin Slide */
    }
    <Layout mdxType="Layout">
      <h1>{`🚗 React Roadmap`}</h1>
      <Steps mdxType="Steps">
        <ul>
          <li parentName="ul">{`Create a static frontend site with `}<strong parentName="li">{`components`}</strong>{` and `}<strong parentName="li">{`props`}</strong>{` (DOM Manipulation)`}</li>
          <li parentName="ul">{`Use `}<strong parentName="li">{`state`}</strong>{` and `}<strong parentName="li">{`events`}</strong>{` to make your site dynamic (Event Handling)`}</li>
          <li parentName="ul">{`Add `}<strong parentName="li">{`side effects`}</strong>{` and `}<strong parentName="li">{`data fetching`}</strong>{` to communicate with a server (Network Communication)`}</li>
        </ul>
      </Steps>
    </Layout>
    <Notes mdxType="Notes">
      <p>{`Your objective during week 1 of React is to learn the fundamentals of React to make a single-page application.
We'll be building something similar to what you made in Phase 3, but with a new set of tools to make that process easier.`}</p>
      <p>{`Learning this will be challenging because you'll need to learn new syntax, as well as learning a new philosophy for how to organize your code.
And since at the end of the day, React is still JavaScript, you'll need your sharpest JS skills to become great React developers.`}</p>
      <p>{`Right now, the most important thing is to start writing code, so in the next part of the lecture we'll get into a little exercise
to practice with components, JSX and props.`}</p>
    </Notes>
    {
      /* End Slide */
    }
    <hr></hr>
    {
      /* Begin Slide */
    }
    <Layout mdxType="Layout">
      <h1>{`🤔 Questions?`}</h1>
    </Layout>
    {
      /* End Slide */
    }
    <hr></hr>
    {
      /* Begin Slide */
    }
    <Layout mdxType="Layout">
      <h1>{`👷‍♀️ Resources`}</h1>
      <ul>
        <li parentName="ul"><a parentName="li" {...{
            "href": "https://reactwithhooks.netlify.app/"
          }}>{`React with Hooks Docs`}</a></li>
        <li parentName="ul"><a parentName="li" {...{
            "href": "https://babeljs.io/repl"
          }}>{`Babel Playground`}</a></li>
        <li parentName="ul"><a parentName="li" {...{
            "href": "http://create-react-app.dev/"
          }}>{`Create React App`}</a></li>
        <li parentName="ul"><a parentName="li" {...{
            "href": "https://overreacted.io/"
          }}>{`Dan Abramov's Blog`}</a></li>
      </ul>
    </Layout>
    {
      /* End Slide */
    }
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;